import React, { useMemo, useEffect, useState } from 'react'
import { useConfirm, confirmDelete, confirmLogout } from '../../components/ConfirmDialog';
import Table from '../../components/Table'
import { connect } from 'react-redux'
import { getAdminsRequested, updateAdminRequested, addAdminRequested, deleteAdminRequested } from '../../actions/adminAction'
import Modal from '../../components/Modal';
import { TrashIcon, PlusCircleIcon, ShieldCheckIcon } from '@heroicons/react/outline'
import { UsersIcon } from '@heroicons/react/solid'
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';

const Admins = ({
    admins,
    getAdminsRequested: getAdmins,
    updateAdminRequested: updateAdmin,
    deleteAdminRequested: deleteAdmin,
    addAdminRequested: addAdmin
}) => {
    const confirm = useConfirm();
    const [showModal, setShowModal] = useState(false);
    const [edit, setEdit] = useState(false);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        getAdmins();
    }, []);

    const columns = useMemo(() => [
        {
            Header: "#",
            Cell: ({ cell }) => (
                <div className="flex items-center space-x-2">
                    <p>{cell.row.index + 1}</p>
                </div>
            ),
        },
        {
            Header: "Name",
            accessor: 'name',
        },
        {
            Header: "Email",
            accessor: 'email',
        },
        {
            Header: "Username",
            accessor: 'username',
        },
        {
            Header: "Cell Leader",
            accessor: 'isCellLeader',
            Cell: ({ value }) => (
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${value ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`}>
                    {value ? 'Yes' : 'No'}
                </span>
            ),
        },
        {
            Header: "Action",
            accessor: 'id',
            idAccessor: 'id',
            Cell: ({ cell, row, column }) => (
                <div className="flex items-center space-x-1">
                    <button
                        className="px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-yellow-100 text-yellow-800"
                        onClick={() => handleEdit(cell.row.original)}
                    >
                        Edit
                    </button>
                    <button
                        className="px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-red-100 text-red-800"
                        onClick={() => handleDeleteAdmin(row.original)}
                    >
                        <TrashIcon className="h-5 w-5" />
                    </button>
                </div>
            ),
        },
    ], []);

    const handleEdit = (data) => {
        setEdit(true);
        setFormData(data);
        setShowModal(true);
    };

    const handleDeleteAdmin = async (admin) => {
        const ok = await confirm({
            title: 'Remove admin?',
            message: `${admin.name} will lose admin access. If they have no other role, their account is deactivated and they can no longer sign in.`,
            confirmLabel: 'Remove admin',
            tone: 'danger',
        });
        if (!ok) return;
        deleteAdmin(admin.id);
    };

    const handleAddAdmin = () => {
        addAdmin(formData);
        setShowModal(false);
        setFormData({});
    }

    const handleOpenModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = (e) => {
        e.preventDefault();
        setFormData({});
        setShowModal(false);
        setEdit(false);
    };

    const handleEditAdmin = (e) => {
        e.preventDefault();
        updateAdmin(formData);
        setFormData({});
        setShowModal(false);
        setEdit(false);
    };

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(values => ({ ...values, [name]: value }))
    }

    const totalAdmins = admins.length;

    return (
        <div className="min-h-screen bg-gray-100 text-gray-900">
            <Modal
                title={edit ? "Edit admin" : "Add admin"}
                description={edit ? undefined : "Admins can manage cells, cell leaders and reports."}
                size="sm"
                submitLabel={edit ? "Save changes" : "Add admin"}
                onClose={handleCloseModal}
                show={showModal}
                onSubmit={edit ? handleEditAdmin : handleAddAdmin}
            >
                <form className='space-y-6'>
                    <div>
                        <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">Name</label>
                        <div className="mt-2">
                            <input
                                id="name"
                                name="name"
                                type="text"
                                required
                                className="px-3 py-2 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6"
                                value={formData?.name || ""}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">Email</label>
                        <div className="mt-2">
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                className="px-3 py-2 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6"
                                value={formData?.email || ""}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="username" className="block text-sm font-medium leading-6 text-gray-900">Username</label>
                        <div className="mt-2">
                            <input
                                id="username"
                                name="username"
                                type="text"
                                required
                                className="px-3 py-2 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6"
                                value={formData?.username || ""}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <input
                            id="isCellLeader"
                            name="isCellLeader"
                            type="checkbox"
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600"
                            checked={!!formData?.isCellLeader}
                            onChange={handleChange}
                        />
                        <label htmlFor="isCellLeader" className="text-sm text-gray-900">
                            <span className="font-medium">Also a cell leader</span>
                            <span className="block text-gray-500">This admin can be assigned to lead one or more cells.</span>
                        </label>
                    </div>
                </form>
            </Modal>
            <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Admins</h1>
                        <p className="text-gray-500 mt-1">Manage admin users</p>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex flex-row items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700 font-semibold px-4 py-2 rounded-lg shadow-md transition-all duration-150"
                        type="button"
                    >
                        <PlusCircleIcon className="h-5 w-5" />
                        Add Admin
                    </button>
                </div>

                {/* Stats Cards */}
                {admins.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <StatCard
                            title="Total Admins"
                            value={totalAdmins}
                            icon={<UsersIcon className="h-6 w-6 text-white" />}
                            bgColor="bg-emerald-500"
                        />
                    </div>
                )}

                {/* Table */}
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    {admins.length > 0 ? (
                        <div className="overflow-x-auto">
                            <Table columns={columns} data={admins} />
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16">
                            <ShieldCheckIcon className="h-16 w-16 text-gray-300 mb-4" />
                            <h2 className="text-xl font-semibold text-gray-700">No admins found</h2>
                            <p className="text-gray-500 mt-1">Get started by adding a new admin</p>
                            <button
                                onClick={() => handleOpenModal()}
                                className="mt-4 flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600 font-medium px-4 py-2 rounded-lg transition-colors"
                            >
                                <PlusCircleIcon className="h-5 w-5" />
                                Add Your First Admin
                            </button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

const StatCard = ({ title, value, icon, bgColor }) => {
    return (
        <div className="bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-sm font-medium mb-1">{title}</p>
                    <p className="text-3xl font-bold text-gray-900">{value}</p>
                </div>
                <div className={`${bgColor} rounded-full p-3`}>
                    {icon}
                </div>
            </div>
        </div>
    );
};

Admins.propTypes = {
    admins: PropTypes.array,
    getAdminsRequested: PropTypes.func.isRequired,
    updateAdminRequested: PropTypes.func.isRequired,
    addAdminRequested: PropTypes.func.isRequired,
    deleteAdminRequested: PropTypes.func.isRequired,
};

Admins.defaultProps = {
    admins: [],
};

const mapStateToProps = (state) => ({
    admins: state.admin.admins,
});

const mapDispatchToProps = dispatch => bindActionCreators({
    getAdminsRequested,
    updateAdminRequested,
    addAdminRequested,
    deleteAdminRequested,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Admins);
